import { resolveRichTextFromGql } from './resolveRichTextFromGql'
import { resolveUpdatedAt } from './gqlMapUtils'
import {
  mapDataStripGql,
  mapDataStripVariablesJsonGql,
} from './mapDataStripGql'
import { mapFaqGql } from './mapFaqGql'

const TYPENAME_TO_CONTENT_TYPE = {
  WwwBlogPosts: 'wwwBlogPosts',
  WwwArticlePages: 'wwwArticlePages',
  WwwDocumentPages: 'wwwDocumentPages',
  WwwDebtConsolidationStatesCities: 'wwwDebtConsolidationStatesCities',
  WwwFile: 'wwwFile',
  WwwClientStories: 'wwwClientStories',
  WwwDisclaimerPages: 'wwwDisclaimerPages',
  WwwIFramePages: 'wwwIFramePages',
}

export const PAGE_MODEL_TYPENAMES = Object.keys(TYPENAME_TO_CONTENT_TYPE)

function withHttpsUrl(url) {
  if (!url) {
    return null
  }

  return url.startsWith('http') ? url : `https:${url}`
}

export function mapGqlAsset(image) {
  if (!image?.url) {
    return null
  }

  const url = withHttpsUrl(image.url)

  return {
    url,
    title: image.title ?? null,
    width: image.width ?? null,
    height: image.height ?? null,
    fileName: image.title ?? '',
    contentType: image.contentType ?? 'image/jpeg',
    details: {
      image: {
        width: image.width ?? null,
        height: image.height ?? null,
      },
    },
  }
}

function mapAssetFields(target, fieldName, image) {
  const asset = mapGqlAsset(image)
  if (!asset) {
    return target
  }

  target[fieldName] = asset.url
  target[`${fieldName}Asset`] = asset

  return target
}

function mapRichText(content) {
  if (!content) {
    return null
  }

  return resolveRichTextFromGql(content)
}

function mapVideoUrl(videoUrl) {
  if (!videoUrl) {
    return null
  }

  return resolveRichTextFromGql(videoUrl)
}

function mapModelContent(model) {
  const richText =
    model.content ??
    model.articleContent ??
    model.documentContent ??
    model.geoContent ??
    model.disclaimerContent

  if (richText) {
    return mapRichText(richText)
  }

  if (model.iframeContent != null) {
    return model.iframeContent
  }

  return null
}

function mapLinkedEntry(entry, extra = {}) {
  if (!entry) {
    return null
  }

  return {
    ...extra,
    key: entry.sys?.id ?? null,
    contentType: entry.__typename
      ? TYPENAME_TO_CONTENT_TYPE[entry.__typename] ??
        entry.sys?.contentTypeId ??
        null
      : entry.sys?.contentTypeId ?? null,
    updatedAt: resolveUpdatedAt(entry),
  }
}

function mapAuthor(author) {
  if (!author) {
    return null
  }

  const mapped = {
    fullName: author.fullName ?? null,
    slug: author.slug ?? null,
    key: author.sys?.id ?? null,
    description: author.description ?? null,
    jobTitle: author.jobTitle ?? null,
    facebook: author.facebook ?? null,
    twitter: author.twitter ?? null,
  }

  if (author.avatar) {
    mapAssetFields(mapped, 'avatar', author.avatar)
  }

  return mapped
}

export function mapCategory(category) {
  if (!category) {
    return null
  }

  const mapped = {
    slug: category.slug ?? null,
    title: category.title ?? null,
    type: category.type ?? null,
    key: category.sys?.id ?? null,
    metaTitle: category.metaTitle ?? null,
    metaDescription: category.metaDescription ?? null,
    order: category.order ?? null,
  }

  if (category.image) {
    mapAssetFields(mapped, 'image', category.image)
  }

  return mapped
}

export function mapJsonLdSchemas(items = []) {
  if (!items?.length) {
    return null
  }

  return items.map((item) => ({
    key: item.key ?? item.slug ?? item.sys?.id ?? null,
    jsonld: item.jsonld ?? item.jsonLd ?? null,
    contentType: 'schemaEntity',
  }))
}

function mapPageModelGql(model) {
  if (!model?.__typename) {
    return null
  }

  const contentType = TYPENAME_TO_CONTENT_TYPE[model.__typename]
  if (!contentType) {
    return null
  }

  const mapped = {
    contentType,
    key: model.sys?.id ?? null,
    title: model.title ?? null,
    metaTitle: model.metaTitle ?? model.title ?? null,
    metaDescription: model.metaDescription ?? null,
    subtitle: model.subtitle ?? null,
    slug: model.slug ?? null,
    follow: model.follow ?? null,
    index: model.index ?? null,
    customPublishedDate: model.customPublishedDate ?? null,
    updatedAt: resolveUpdatedAt(model),
    createdAt: model.sys?.createdAt ?? null,
    shortDescription: model.shortDescription ?? null,
    description: model.description ?? null,
    content: mapModelContent(model),
    articleContent: mapRichText(model.articleContent),
    text: model.text ?? null,
    link: model.link ?? null,
    script: model.script ?? null,
    videoUrl: mapVideoUrl(model.videoUrl),
    externalLink: model.externalLink ?? null,
    state: model.state ?? null,
    city: model.city ?? null,
    demonym: model.demonym ?? null,
    author: mapAuthor(model.author),
    reviewedBy: mapAuthor(model.reviewedBy),
    category: mapCategory(model.category),
    jsonLdSchemas: mapJsonLdSchemas(model.jsonLdSchemasCollection?.items),
    keyTakeaways: model.keyTakeaways ?? null,
    faqTitle: model.faqTitle ?? null,
    faq: mapFaqGql(model.faqCollection?.items ?? []),
    dataStrip: mapDataStripGql(model.dataStrip),
    dataStripVariablesJson: mapDataStripVariablesJsonGql(
      model.dataStripVariablesJson
    ),
  }

  mapAssetFields(mapped, 'image', model.image)
  mapAssetFields(mapped, 'coverPhoto', model.coverPhoto)
  mapAssetFields(mapped, 'flagIcon', model.flagIcon)
  mapAssetFields(mapped, 'flagseal', model.flagseal)

  if (model.file) {
    const fileAsset = mapGqlAsset(model.file)
    if (fileAsset) {
      mapped.file = fileAsset.url
      mapped.fileAsset = {
        ...fileAsset,
        contentType: model.file.contentType ?? fileAsset.contentType,
        fileName: model.file.title ?? fileAsset.fileName,
      }
    }
  }

  return mapped
}

/** True when Delivery API resolved a model that mapPageDataGql can render. */
export function isRenderablePageModel(model) {
  return Boolean(mapPageModelGql(model)?.key)
}

/** Map Contentful GraphQL wwwPages item → PageRepository.getAllData shape. */
export function mapPageDataGql(item) {
  if (!item) {
    return null
  }

  const model = mapPageModelGql(item.model)
  if (!model?.key) {
    return null
  }

  return {
    path: item.path ?? null,
    locale: item.locale ?? null,
    template: item.template ?? null,
    isRestricted: item.isRestricted === true,
    restrictionValue: item.restrictionValue ?? null,
    follow: item.follow ?? model.follow ?? null,
    index: item.index ?? model.index ?? null,
    updatedAt: resolveUpdatedAt(item) ?? resolveUpdatedAt(model) ?? null,
    category: mapCategory(item.category) ?? model.category ?? null,
    key: item.sys?.id ?? null,
    contentType: 'wwwPages',
    model,
    jsonLdSchemas: mapJsonLdSchemas(item.jsonLdSchemasCollection?.items),
  }
}
